
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Tabs, Spin, TabsProps } from 'antd';
import { FormattedMessage } from 'react-intl';
import { isEmpty } from 'lodash';

import { Reservations, MyTravelHistory, MyProfile, ChangePassword, MyQuotes, MySideKickRewards, MyAccount, SavedProperty, AccountHub } from '@components';
import { ILoginState, IMenuState } from '@share/store/slices';
import { isQuotesFromPath, getAccountUsernameFromPath, isTestDriveManagerFromPath, isReservationsFromPath, isSavedPropertyFromPath, isDisplayAccountHub, LoginType, FunctionalRootState } from '@share/utils';
import { Loading } from '@share/components';
import { Routes } from '@share/constants';
import { ISideKickStatus } from '@share/common-types';

import HotelBackground from '@assets/images/hotel/bg-hotels.png';

import './style.scss';


export function AccountPage() {

  const loginStore: ILoginState = useSelector((state: FunctionalRootState) => state.loginStore);
  const menuStore: IMenuState = useSelector((state: FunctionalRootState) => state.navigationMenuStore);

  const history = useHistory();

  const { account, loading } = loginStore;
  const { items } = menuStore;

  const [activeKey, setActiveKey] = useState('1');

  const styleAccount: any = { backgroundImage: `linear-gradient(rgba(255, 255, 255, 0) 100%, rgb(255, 255, 255)), url(${HotelBackground})` };
  const isParentSidekick = items?.sideKickStatus === ISideKickStatus.PARENT;

  const isQuotes = useMemo(() => isQuotesFromPath(history), [history]);
  const isTestDriveManager = useMemo(() => isTestDriveManagerFromPath(history), [history]);
  const isReservations = useMemo(() => isReservationsFromPath(history), [history]);
  const isSavedProperty = useMemo(() => isSavedPropertyFromPath(history), [history]);

  useEffect(() => {
    handleChange();
  }, []);

  const handleChange = () => {
    if (!account || account.type !== LoginType.PrivateWithToken) {
      history.push(Routes.NotAuthorized);
    }
    
    if (isQuotes) {
      setActiveKey('5');
    } else if (isTestDriveManager) {
      setActiveKey('7');
    } else if (isReservations) {
      setActiveKey('3');
    } else if (isSavedProperty) {
      setActiveKey('8');
    } else {
      setActiveKey('1');
    }
  }

  const handleTab = (key: string) => {
    setActiveKey(key);
    const account = getAccountUsernameFromPath(history);
    if (key === '8') {
      history.push(`/${account}${Routes.SavedProperties}`);
    } else if (key !== '6') {
      history.push(`/${account}${Routes.RsiAccount}${location.search}`);
    } else {
      history.push(`/${account}${Routes.RsiQuotes}`);
    }
  }

  const isPrivateWithToken = account?.type === LoginType.PrivateWithToken;
  const privateTokenFromUrl = account?.privateTokenFromUrl;

  const getItemsCapital = useCallback(() => {
    const tabsItems: TabsProps['items'] = [
      { key: '1', label: <FormattedMessage id="my.account.title" />, children: <MyAccount /> },
      { key: '2', label: <FormattedMessage id="reservation.search.title" />, children: <Reservations /> }
    ];
    return tabsItems;
  }, []);

  if (isPrivateWithToken && privateTokenFromUrl) {
    return (
      <div className="account-page" style={{ flex: '1' }}>
        {account?.isCapitalVacations ? (
          <div className="account-page__background-wrapper">
            <div className="account-page__account-background" style={styleAccount}></div>
            <div className="account-page__wrapper">
              <h1 className="account-page__header secondary-font"><FormattedMessage id={account?.isCapitalVacationsCondoRes ? 'reservation.search.title' : 'my.account.title'} /></h1>
            </div>
          </div>) : null}

        <Spin spinning={loading} indicator={<Loading />} >
          <div className="container account-page__account-container" style={{ paddingTop: '20px' }}>
            {(account?.isCapitalVacations && !account?.isCapitalVacationsCondoRes) ? (
              <Tabs defaultActiveKey="1" items={getItemsCapital()} activeKey={activeKey} onTabClick={handleTab}>
              </Tabs>) : (
              <Reservations />)}
          </div>
        </Spin>
      </div>
    );
  }

  // Redesigned hub for enabled tenants; never for Capital Vacations (see isDisplayAccountHub).
  if (isDisplayAccountHub(account)) {
    return <AccountHub />;
  }

  const organizationId = items?.organizationId;
  const siteId = items?.siteId;

  const isRSITemplate = account?.isRSITemplate;
  const isPublicAccountType = account?.isPublicAccountType;
  const displaySavedProperties = account?.displaySavedProperties;
  const displaySavedPropertiesRsiSiteidList = account?.displaySavedPropertiesRsiSiteidList;
  const displaySavedPropertiesRsiOrgidList = account?.displaySavedPropertiesRsiOrgidList;

  const notAuth = false;//isRSITemplate && user && items && !loginStore?.loading && !menuStore?.loading && !showMyAccount;
  
  if (notAuth && !isQuotes) {
    history.push({
      pathname: Routes.CustomError,
      state: {
        data: 'UNAUTHORIZED',
      },
    });

    return null;
  }
  
  const emptyOrgIdList = !!displaySavedPropertiesRsiOrgidList?.length;
  const emptySiteIdList = !!displaySavedPropertiesRsiSiteidList?.length;

  const isOrgId = displaySavedPropertiesRsiOrgidList?.includes(organizationId?.toString() as string);
  const isSiteId = displaySavedPropertiesRsiSiteidList?.includes(siteId?.toString() as string);
  
  const displayRsi = isRSITemplate && ((!emptyOrgIdList && !emptySiteIdList) || isOrgId || isSiteId)
  const displayRest = !isRSITemplate;

  const displaySavedPropertiesTab = !isPublicAccountType && displaySavedProperties && (displayRsi || displayRest);
  
  // Plain function, not useCallback: it runs after the early returns above, and hooks must not.
  // Tab rules are unchanged from before the hub (the hub has its own list in account-modern/utils).
  const getItems = () => {
    const tabsItems: TabsProps['items'] = [
      { key: '1', label: <FormattedMessage id="profile" />, children: <MyProfile /> }
    ];

    if (!items?.isMLM) {
      tabsItems.push({ key: '2', label: <FormattedMessage id="manage.password" />, children: <ChangePassword /> });
    }

    if (!isRSITemplate || !account?.isMergeHistoryTabs) {
      tabsItems.push({ key: '3', label: <FormattedMessage id={items?.isMLM ? 'reservation.search.title' : 'my.active.hotel.reservations'} />, children: <Reservations /> });
    }
    
    if (isRSITemplate && !items?.isMLM) {
      tabsItems.push({ key: '4', label: <FormattedMessage id="my.travel.history" />, children: <MyTravelHistory /> });
      tabsItems.push({ key: '5', label: <FormattedMessage id="my.quotes" />, children: <MyQuotes /> });
    }

    if (isParentSidekick) {
      tabsItems.push({
        key: '6',
        label: !isEmpty(items?.sideKickName) ? items.sideKickName : <FormattedMessage id={!items?.isMLM ? 'my.sidekicks' : 'test.drive.manager'} />,
        children: <MySideKickRewards /> });
    }

    if (displaySavedPropertiesTab) {
      tabsItems.push({ key: '8', label: <FormattedMessage id="saved.properties" />, children: <SavedProperty /> });
    }

    return tabsItems;
  };

  return (
    <div className="account-page">
      <div className="account-page__background-wrapper">
        <div className="account-page__account-background" style={styleAccount}></div>
        <div className="account-page__wrapper">
          <h1 className="account-page__header secondary-font"><FormattedMessage id={(notAuth && isQuotes) ? 'my.quotes' : 'my.account.title'} /></h1>
        </div>
      </div>

      <Spin spinning={loading} indicator={<Loading />} >
        <div className="container account-page__account-container">
          {!notAuth ? (
            <Tabs defaultActiveKey="1" items={getItems()} activeKey={activeKey} onTabClick={handleTab}>
            </Tabs>) : (
            <MyQuotes />)}
        </div>
      </Spin>
    </div>
  );
}
